import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageActivityEvent } from "@/services/bedrock/dtos/DesignPackageActivity";
import { designKeys } from "@/services/bedrock/queryKeys";

const PER_PAGE = 30;

function byNewest(a: DesignPackageActivityEvent, b: DesignPackageActivityEvent): number {
  const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  return diff !== 0 ? diff : b.id - a.id;
}

/** Pages of the activity log; the channel hook prepends live events into page 1. */
export function useActivity(packageId: number | null) {
  const query = useInfiniteQuery({
    queryKey: designKeys.activity(packageId ?? 0),
    queryFn: ({ pageParam }) =>
      DesignPackageServices.activity(packageId as number, { page: pageParam, perPage: PER_PAGE }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const totalPages = last.pagination.totalPages ?? 1;
      return pages.length < totalPages ? pages.length + 1 : undefined;
    },
    enabled: packageId != null,
  });

  const { newestFirst, oldestFirst } = useMemo(() => {
    const seen = new Set<number>();
    const events = (query.data?.pages ?? [])
      .flatMap(page => page.content)
      .filter(event => (seen.has(event.id) ? false : (seen.add(event.id), true)))
      .sort(byNewest);
    return { newestFirst: events, oldestFirst: [...events].reverse() };
  }, [query.data]);

  return {
    /** Ascending, for the thread. */
    events: oldestFirst,
    /** Descending, for the activity panel. */
    eventsNewestFirst: newestFirst,
    isLoading: query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
